import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  List,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  Assessment as ReportingIcon,
  Business as AdminIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ThumbUp as ApprovalIcon,
  Folder as ResourcesIcon,
} from '@mui/icons-material';
import DirectoryIcon from './DirectoryIcon';
import { useAuth } from '../../context/AuthContext';
import {
  SidebarRoot,
  Logo,
  StyledListItem,
  SubMenuItem,
} from '../../styles/Sidebar.styles';

const Sidebar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({
    administration: false,
    system: false,
    timecards: false,
  });

  const handleMenuClick = (menu) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };

  const isAdmin = user?.role === 'admin';
  const isTeamLead = user?.role === 'team_lead';
  const isUser = user?.role === 'user';
  const isAccountant = user?.role === 'accountant';

  let navigationItems = [];

  // Regular user and team lead have Timecard
  if (isUser || isTeamLead) {
    navigationItems.push({
      title: 'Timecard',
      icon: <TimeIcon />,
      path: '/timecard',
    });
  }

  // All roles have Reporting
  navigationItems.push({
    title: 'Reporting',
    icon: <ReportingIcon />,
    path: '/reporting',
  });

  // All roles except Accountant have Directory
  if (!isAccountant) {
    navigationItems.push({
      title: 'Directory',
      icon: <DirectoryIcon />,
      path: '/directory',
    });
  }

  // All roles except Accountant have Resources
  if (!isAccountant) {
    navigationItems.push({
      title: 'Resources',
      icon: <ResourcesIcon />,
      path: '/resources',
    });
  }

  // Only team lead has Hours Approval
  if (isTeamLead) {
    navigationItems.push({
      title: 'Hours Approval',
      icon: <ApprovalIcon />,
      path: '/hours-approval',
    });
  }

  // Admin and team lead have Administration dropdown
  if (isAdmin || isTeamLead) {
    navigationItems.push({
      id: 'administration',
      title: 'Administration',
      icon: <AdminIcon />,
      submenu: [
        { title: 'Customers', path: '/customers' },
        { title: 'Projects', path: '/projects' },
        { 
          id: 'timecards',
          title: 'Timecards',
          submenu: [
            { title: 'Create Timecards', path: '/create-timecards' },
            { title: 'Edit Timecards', path: '/edit-timecards' }
          ]
        },
      ],
    });
  }

  // Admin and team lead have System dropdown
  if (isAdmin || isTeamLead) {
    navigationItems.push({
      id: 'system',
      title: 'System',
      icon: <SettingsIcon />,
      submenu: [
        { title: 'Users', path: '/users' },
        { title: 'Teams', path: '/teams' },
        { title: 'Settings', path: '/settings' },
      ],
    });
  }

  return (
    <SidebarRoot>
      <Logo>ECI Time Portal</Logo>
      <List component="nav">
        {navigationItems.map((item) => (
          <React.Fragment key={item.title}>
            {item.submenu ? (
              <>
                <StyledListItem
                  button
                  onClick={() => handleMenuClick(item.id)}
                  active={item.submenu.some(sub => location.pathname === sub.path)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.title} />
                  {openMenus[item.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </StyledListItem>
                <Collapse in={openMenus[item.id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.submenu.map((subItem) => (
                      <React.Fragment key={subItem.title}>
                        {subItem.submenu ? (
                          <>
                            <SubMenuItem
                              button
                              onClick={() => handleMenuClick(subItem.id)}
                            >
                              <ListItemText primary={subItem.title} />
                              {openMenus[subItem.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </SubMenuItem>
                            <Collapse in={openMenus[subItem.id]} timeout="auto" unmountOnExit>
                              <List component="div" sx={{ pl: 2 }}>
                                {subItem.submenu.map((nestedItem) => (
                                  <SubMenuItem
                                    key={nestedItem.title}
                                    button
                                    active={location.pathname === nestedItem.path}
                                    onClick={() => navigate(nestedItem.path)}
                                  >
                                    <ListItemText primary={nestedItem.title} />
                                  </SubMenuItem>
                                ))}
                              </List>
                            </Collapse>
                          </>
                        ) : (
                          <SubMenuItem
                            button
                            active={location.pathname === subItem.path}
                            onClick={() => navigate(subItem.path)}
                          >
                            <ListItemText primary={subItem.title} />
                          </SubMenuItem>
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              <StyledListItem
                button
                active={location.pathname === item.path}
                onClick={() => navigate(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.title} />
              </StyledListItem>
            )}
          </React.Fragment>
        ))}
      </List>
    </SidebarRoot>
  );
};

export default Sidebar;
